// Types mirror the server's JSON; the OpenAPI document at /api/docs is the
// source of truth.

import type { RankedEffort, Zones } from '../utils/training';

// GroupSharing is what the viewer shares with one of their groups, and for
// each item they do not, who in it still sees it through another group
// (spec 001 FR-047).
export type SharingItem = 'location' | 'activities' | 'heart_rate' | 'health' | 'rest_sleep' | 'driving' | 'phone' | 'email';
// LocationChoice is how much of the member's location a group sees, most
// detailed first (spec 001 FR-048).
export type LocationChoice = 'detailed' | 'standard' | 'choose' | 'off';
export interface GroupSharing {
  group_id: string;
  managed: boolean;
  // location is how much of the member's location the group sees now, and
  // detailed_until when a timed detailed choice ends (spec 001 FR-049).
  location: LocationChoice;
  detailed_until?: string;
  activities: boolean;
  heart_rate: boolean;
  // health is steps and active minutes (spec 006 FR-101).
  health: boolean;
  // rest_sleep is the latest resting heart rate and last night's sleep (spec 007 FR-027).
  rest_sleep: boolean;
  // driving is the driving report: each drive's counts and the week's (spec 001 FR-061).
  driving: boolean;
  phone: boolean;
  email: boolean;
  // lookback_days is how far back the group sees: today and this many days
  // before, or 0 for forever (spec 012 FR-043).
  lookback_days: number;
  still_seen: Partial<Record<SharingItem, { user_id: string; display_name: string }[]>>;
}

// PersonEntry is someone the member shares a group with (spec 001 FR-079).
export interface PersonEntry {
  user_id: string;
  display_name: string;
  groups: string[];
}

// PersonView is what one person gets of the member across their shared
// groups (spec 001 FR-076, 001 FR-077, 001 FR-078). A paused group gives no location,
// history, activities, or driving while the pause lasts.
export interface PersonView {
  user_id: string;
  display_name: string;
  groups: { group_id: string; name: string; paused: boolean; paused_until?: string }[];
  location: { level: LocationChoice | 'paused'; groups: string[]; detailed_until?: string };
  lookback: { days: number; groups: string[] };
  items: Record<'activities' | 'heart_rate' | 'health' | 'rest_sleep' | 'driving' | 'phone' | 'email', string[]>;
}

// SharingDefaults is what the member's new groups start with (spec 001 FR-072).
export interface SharingDefaults {
  location: LocationChoice;
  activities: boolean;
  heart_rate: boolean;
  health: boolean;
  rest_sleep: boolean;
  driving: boolean;
  phone: boolean;
  email: boolean;
  lookback_days: number;
  set: boolean;
}

export interface Group {
  id: string;
  name: string;
  created_by: string;
  // created_by_name tells groups of one name apart (spec 001).
  created_by_name?: string;
  created_at: string;
  role: 'admin' | 'member';
  managed: boolean;
  // location is how much of the caller's location the group sees (spec 001).
  location: LocationChoice;
  detailed_until?: string;
  paused_since?: string;
  paused_until?: string;
}

export interface Member {
  user_id: string;
  username: string;
  display_name: string;
  role: 'admin' | 'member';
  managed: boolean;
  joined_at: string;
  crash_detection: boolean;
  paused_since?: string;
  paused_until?: string;
  /** tracking_mode is the member's level as it applies to the viewer: manual for When I choose, detailed, or absent (spec 001 FR-060). */
  tracking_mode?: 'manual' | 'detailed';
}

export interface Fix {
  recorded_at: string;
  received_at: string;
  lat: number;
  lon: number;
  accuracy_m: number;
  speed_mps?: number;
  heading_deg?: number;
  activity?: string;
  battery_pct?: number;
  charging?: boolean;
}

export type MemberStatus = 'current' | 'stale' | 'limited' | 'paused' | 'unknown' | 'not_shared';

export interface MemberLocation {
  user_id: string;
  display_name: string;
  // groups are the viewer's groups the member is in, for the map's filter (spec 001 FR-038).
  groups: string[];
  status: MemberStatus;
  reason?: string;
  last_seen_at?: string;
  location?: Fix;
  paused_since?: string;
  paused_until?: string;
  /** wake is the latest refresh of the member's phone that has had no answer. */
  wake?: { asked_at: string; error: '' | 'no_push' | 'push_refused' | 'budget' | 'signed_out' };
  /** activity is one the member is recording now (spec 006). */
  activity?: LiveActivity;
  /** tracking_mode is manual when the member shares with the viewer only when they choose, detailed when the viewer sees every fix. */
  tracking_mode?: 'manual' | 'detailed';
  /** place is the group's place the member is in, with its ID and icon. */
  place?: string;
  place_id?: string;
  place_icon?: PlaceIcon;
  /** since is when the member arrived where they are now; absent while moving or unknown. */
  since?: string;
  /** near is the road or address the member is at or near, once the server has named it. */
  near?: string;
  /** color is the member's own color, drawn behind their initials. */
  color: string;
  /** photo is the version of the member's photo; absent without one. */
  photo?: string;
  crash_detection: boolean;
  /** phone is the member's number when they share it with their groups. */
  phone?: string;
  /** email is the member's address when they share it with their groups. */
  email?: string;
  /** heading is where the member said they are heading, for viewers who see them all the time (spec 003). */
  heading?: Destination;
  /** destination is the viewer's own destination for the member (spec 003). */
  destination?: Destination;
  /** health_summary is the member's latest resting heart rate and last night's sleep, when they share it (spec 007 FR-027). */
  health_summary?: HealthSummary;
}

// HealthSummary is what a group with Resting heart rate and sleep sees of a
// member's health history (spec 007 FR-027).
export interface HealthSummary {
  resting_hr?: number;
  resting_day?: string;
  sleep?: { asleep_s: number; started_at: string; ended_at: string };
}

// Destination is where a member is heading, as the viewer sees it (spec 003).
export interface Destination {
  id: string;
  kind: 'heading' | 'watch';
  name: string;
  lat: number;
  lon: number;
  place_id?: string;
  set_at: string;
  ends_at: string;
  /** watching is whether the viewer is told when the member arrives. */
  watching: boolean;
  eta?: ETA;
}

// ETA is how long until a member reaches a destination, from one of their fixes.
export interface ETA {
  kind: 'road' | 'estimate';
  mode: 'driving' | 'cycling' | 'walking';
  seconds: number;
  distance_m: number;
  /** moving is false before the member sets off; the time is then as if they left now. */
  moving: boolean;
  as_of: string;
  arrive_at: string;
  /** route is the rest of the way as [lon, lat] pairs. */
  route: [number, number][];
}

// RoadData is the server's road data for server admins (spec 003 FR-032).
export interface RoadData {
  state: 'off' | 'empty' | 'fetching' | 'building' | 'ready';
  areas: MapArea[];
}

// AreaChoice is an area a server admin can add (spec 003 FR-032).
export interface AreaChoice {
  id: string;
  name: string;
  region: string;
}

export interface MapArea {
  id: string;
  name: string;
  bytes: number;
  fetched_at: string | null;
  last_used_at: string;
  kept: boolean;
  roads_at: string | null;
  tiles_at: string | null;
}

export type PlaceIcon = 'home' | 'school' | 'work' | 'shopping' | 'sport' | 'health' | 'family' | 'pin';

// ActivityType is a sport ID of the catalog (spec 006).
export type ActivityType = string;

// LiveActivity is an activity being recorded, as the map and the group's
// socket carry it.
export interface LiveActivity {
  id: string;
  user_id?: string;
  type: ActivityType;
  state: 'recording' | 'paused' | 'finished';
  started_at: string;
  ended_at?: string;
  distance_m: number;
  elapsed_s: number;
  moving_s?: number;
  progress_at?: string;
}

export interface ActivityPoint {
  at?: string;
  lat: number;
  lon: number;
  ele_m?: number;
}

export interface RouteSegment {
  gap_before?: boolean;
  points: ActivityPoint[];
}

export interface Activity {
  id: string;
  user_id: string;
  source: 'recorded' | 'imported' | 'watch' | 'automatic';
  type: ActivityType;
  /** gps is false for a timer-only sport, which has no route, distance, pace, or climb. */
  gps: boolean;
  name: string;
  /** device_name is the phone it was recorded on, named at the finish; empty for imports. */
  device_name: string;
  /** start_place is the town it started in, once the server has named it. */
  start_place?: string;
  state: 'recording' | 'paused' | 'finished';
  started_at: string;
  ended_at?: string;
  timed: boolean;
  distance_m: number;
  elapsed_s: number;
  moving_s: number;
  elevation_gain_m: number;
  // steps is the pedometer's count on a foot sport, and distance_estimated marks a distance from steps (spec 006).
  steps?: number;
  distance_estimated?: boolean;
  /** no_route marks a workout of a sport with a map that was imported without a route (spec 006 FR-117). */
  no_route?: boolean;
  splits_km: number[];
  splits_mi: number[];
  pauses: { started_at: string; ended_at?: string }[];
  progress_at?: string;
  route?: RouteSegment[];
  /** hr_avg, hr_max, their per-split averages, and calories are absent when unknown or not shared (spec 006). */
  hr_avg?: number;
  hr_max?: number;
  hr_splits_km?: (number | null)[];
  hr_splits_mi?: (number | null)[];
  calories?: number;
  /** active_minutes are the activity's moderate minutes plus twice its vigorous ones (spec 006). */
  active_minutes?: number;
  /** companions are who it was done with, as far as the viewer may see them; only on a single activity. */
  companions?: Companion[];
  /** zones are the seconds in each heart rate zone; with heart rate, and for a group only when shared (spec 006). */
  zones?: Zones;
  /** load is the training load, estimated from moving time without heart rate; the owner's only. */
  load?: number;
  load_estimated?: boolean;
  /** efforts are this activity's best efforts that rank in the owner's top 3 for the sport. */
  efforts?: RankedEffort[];
}

// Companion is a member an activity was done together with (spec 006 FR-074).
export interface Companion {
  user_id: string;
  display_name: string;
  photo?: string;
  /** activity_id is their own activity, for a match; absent for someone added by hand. */
  activity_id?: string;
  /** group_id opens activity_id as a group sees it; absent when it is the viewer's own. */
  group_id?: string;
  added: boolean;
}

export interface Invite {
  id: string;
  group_id: string;
  expires_at: string;
  created_at: string;
  url?: string;
}

// ReusedSignInLink is a sign-in link someone tried to use a second time.
export interface ReusedSignInLink {
  id: string;
  created_at: string;
  used_at?: string;
}

export interface SignInLink {
  url: string;
  expires_at: string;
  server_url: string;
  username: string;
}

// AuthSession is one of the account's live sessions, named by the device
// signed in with it or by its browser (spec 001 FR-119).
export interface AuthSession {
  id: string;
  ip_address: string;
  last_active_at: string;
  // expires_at is when the session ends if left unused (001 FR-120).
  expires_at: string;
  created_at: string;
  current: boolean;
  kind: 'app' | 'web';
  device_id?: string;
  device_name?: string;
  device_kind?: string;
  browser?: string;
  system?: string;
}

// ApiKey is one of the account's API keys, without its value (spec 001 FR-115).
export interface ApiKey {
  id: number;
  name: string;
  // prefix is the value's first 8 characters.
  prefix: string;
  expires_at?: string;
  last_used?: string;
  created_at: string;
}

export interface Device {
  id: string;
  name: string;
  platform: string;
  // kind is phone, tablet, or tracker (spec 011).
  kind: 'phone' | 'tablet' | 'tracker' | 'computer';
  flavor: string;
  app_version: string;
  last_seen_at: string;
  created_at: string;
  capabilities: Record<string, unknown>;
  // primary marks the device whose arrivals and departures count (003 FR-006).
  primary: boolean;
}

export interface MapInfo {
  light_style_url: string;
  dark_style_url: string;
  attribution: string;
  available: boolean;
  // attribution_optional lets the member hide map credits in this browser (spec 009).
  attribution_optional?: boolean;
}

export interface Place {
  id: string;
  group_id: string;
  name: string;
  lat: number;
  lon: number;
  radius_m: number;
  icon: PlaceIcon;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// PlaceEvent is an arrival, a departure, or a trip end (spec 003) in a
// group's history. A trip end's place_id is the group's place it ended in.
export interface PlaceEvent {
  id: string;
  group_id: string;
  place_id?: string;
  user_id: string;
  kind: 'arrive' | 'depart' | 'trip_end';
  occurred_at: string;
  detected_at: string;
  source?: 'device' | 'server';
  late: boolean;
  trip?: TripInfo;
}

export interface TripInfo {
  started_at: string;
  distance_m: number;
  duration_s: number;
  mode: TravelMode;
  address?: string;
}

export type TripKind = 'driving' | 'walking' | 'running' | 'cycling' | 'other';
export const TRIP_KINDS: TripKind[] = ['driving', 'walking', 'running', 'cycling', 'other'];

export interface AlertRule {
  id: string;
  place_id: string | null;
  place_name: string | null;
  on_arrive: boolean;
  on_depart: boolean;
}

// AlertSetting is what the viewer hears about one member, or everyone when
// subject_id is null, in one group.
export interface AlertSetting {
  subject_id: string | null;
  trip_ends: TripKind[];
  // phone is hearing when the member's phone is low or stops reporting (spec 003).
  phone?: boolean;
  places: AlertRule[];
}

export interface MyAlertSetting extends AlertSetting {
  group_id: string;
  group_name: string;
  subject_name: string | null;
}


export interface RefreshResult {
  asked: string[];
  skipped: { user_id: string; reason: 'fresh' | 'coalesced' | 'budget' | 'unreachable' }[];
}

export interface LiveJoined {
  session_id: string;
  topic: string;
  state: 'waiting' | 'streaming' | 'unreachable';
  reason?: string;
  near?: string;
}

export interface Person {
  user_id: string;
  display_name: string;
}

export interface CrashSettings {
  enabled: boolean;
  recipients: Person[];
}

export interface CrashSender extends Person {
  enabled: boolean;
}

export interface CrashDetails {
  id: string;
  user_id: string;
  display_name: string;
  detected_at: string;
  lat: number;
  lon: number;
  accuracy_m: number;
  state: 'prompting' | 'cancelled' | 'alerted' | 'resolved_ok';
  phone?: string;
  group_id?: string;
}

// CheckIn is a check-in timer (specs/004-safety/contracts/README.md): the
// member's own, or one that names the viewer.
export interface CheckIn {
  id: string;
  user_id: string;
  display_name: string;
  note: string;
  started_at: string;
  due_at: string;
  // closed is how an alert nobody resolved reads to the people it named a
  // day after it went out.
  state: 'running' | 'checked_in' | 'alerted' | 'resolved_ok' | 'closed';
  alerted_at?: string;
  resolved_at?: string;
  late: boolean;
  lat?: number;
  lon?: number;
  accuracy_m?: number;
  located_at?: string;
  phone?: string;
  group_id?: string;
  recipients?: { user_id: string; display_name: string }[];
  // alerted is who the alert went to, for the member, after an alert.
  alerted?: { user_id: string; display_name: string }[];
  // A place-tied timer (spec 003) watches for leaving or reaching watch_name.
  watch?: 'leave' | 'arrive';
  watch_name?: string;
  auto_at?: string;
  watch_note?: string;
}

// ArriveByAlert tells its setter when a member isn't at a place by a time (spec 003).
export interface ArriveByAlert {
  id: string;
  group_id: string;
  place_id: string;
  place_name: string;
  setter_id: string;
  setter_name: string;
  member_id: string;
  member_name: string;
  time: string;
  tz: string;
  date?: string;
  weekdays: number[];
  next_due_at?: string;
  last_result: '' | 'ok' | 'missing' | 'unchecked';
}

// SosAlert is an SOS as its member or a person it paged sees it (spec 004).
export interface SosAlert {
  id: string;
  user_id: string;
  display_name: string;
  source: 'phone' | 'watch' | 'web' | 'computer';
  sent_at: string;
  received_at: string;
  late: boolean;
  state: 'open' | 'safe' | 'closed';
  resolved_at?: string;
  lat?: number;
  lon?: number;
  accuracy_m?: number;
  located_at?: string;
  phone?: string;
  group_id?: string;
  recipients?: Person[];
}

// NotificationItem is a notification with the push message it carried.
export interface NotificationItem {
  id: string;
  kind: string;
  tag: string;
  created_at: string;
  message: { type: string; title?: string; body?: string; at?: string; ref?: string };
}

export interface RoutePoint {
  at: string;
  lat: number;
  lon: number;
  accuracy_m?: number;
  ele_m?: number;
}

export interface PauseResult {
  paused: string[];
  refused: string[];
  // still_seeing names who still sees the member through a group not paused (spec 001 FR-082).
  still_seeing: { user_id: string; display_name: string }[];
}

export type PlaceCategory =
  | 'food_drink' | 'shopping' | 'attractions' | 'culture' | 'lodging' | 'work_education'
  | 'health' | 'outdoors' | 'transport' | 'home' | 'other';

export type TravelMode = 'walking' | 'running' | 'cycling' | 'driving' | 'bus' | 'train' | 'flight' | 'other';

export interface TimelineStay {
  id?: string;
  start: string;
  end: string;
  tz: string;
  lat: number;
  lon: number;
  place_id?: string;
  name?: string;
  category?: PlaceCategory;
  address?: string;
  city?: string;
  region?: string;
  country_code?: string;
  country?: string;
  source: 'kehai' | 'import' | 'member';
  edited?: boolean;
  ongoing?: boolean;
  // icon is set for a stay at one of the group's places (spec 010).
  icon?: PlaceIcon;
}

export interface TimelineTrip {
  id?: string;
  start: string;
  end: string;
  tz: string;
  mode: TravelMode;
  distance_m: number;
  gap?: boolean;
  missing_visit?: boolean;
  detail_m?: number;
  source: 'kehai' | 'import' | 'member';
  edited?: boolean;
  ongoing?: boolean;
  // top_speed_mps is the trip's backed-up top speed (specs/010-app/research.md).
  top_speed_mps?: number;
  route: RoutePoint[];
  // driving is the driving report's summary for a drive (spec 004).
  driving?: DrivingSummary;
}

// Drives sums a member's drives since a day (spec 010 FR-016).
export interface Drives {
  from: string;
  drives: number;
  distance_m: number;
  seconds: number;
  top_speed_mps?: number;
  top_speed_at?: string;
  partial: boolean;
  // behavior is the driving report's weekly counts (spec 004).
  behavior?: Behavior;
}

// DrivingKind is an event the driving report counts (spec 004).
export type DrivingKind = 'speeding' | 'hard_brake' | 'rapid_accel' | 'phone_use';

export interface Measure {
  ok: boolean;
  reason?: string;
}

// DrivingSummary is what a drive's card says about driving events.
export interface DrivingSummary {
  passenger: boolean;
  counts: Partial<Record<DrivingKind, number>>;
  measured: Partial<Record<DrivingKind, Measure>>;
  speeding_pending?: boolean;
  waiting?: boolean;
}

export interface Coverage {
  drives: number;
  of: number;
}

export interface Behavior {
  this_week: Partial<Record<DrivingKind, number>>;
  last_week: Partial<Record<DrivingKind, number>>;
  measured: Partial<Record<DrivingKind, Coverage>>;
  last_measured: Partial<Record<DrivingKind, Coverage>>;
}

// DrivingEvent is one of the member's own events, with its place and time.
export interface DrivingEvent {
  kind: DrivingKind;
  started_at: string;
  ended_at: string;
  lat: number;
  lon: number;
  value: number;
  rule?: 'over_80' | 'posted';
  limit_mps?: number;
}

export interface TimelineGap {
  kind: 'missing_visit' | 'paused';
  start: string;
  end: string;
}

export interface TimelineDay {
  date: string;
  tz: string;
  simplified: boolean;
  stays: TimelineStay[];
  trips: TimelineTrip[];
  gaps: TimelineGap[];
  // The member's own day carries what only they see (spec 005).
  notes?: TimelineNote[];
  categories?: { category: PlaceCategory; seconds: number }[];
  travel_s?: number;
  unrecorded_s?: number;
}

// TimelineNote is the member's note on a day, a stay, or a trip (spec 005 FR-042).
export interface TimelineNote {
  id: string;
  date: string;
  stay_id?: string;
  trip_id?: string;
  text: string;
  updated_at: string;
}

export interface ModeTotal {
  mode: TravelMode;
  distance_m: number;
  seconds: number;
}

export interface CategoryTotal {
  category: PlaceCategory;
  seconds: number;
  places: number;
}

export interface Insights {
  period: string;
  modes: ModeTotal[];
  categories: CategoryTotal[];
  months: { month: string; modes: ModeTotal[] }[];
}

export interface PlaceSummary {
  id?: string;
  name?: string;
  category: PlaceCategory;
  address?: string;
  city?: string;
  country_code?: string;
  country?: string;
  lat: number;
  lon: number;
  visits: number;
  seconds: number;
  first_visit: string;
  last_visit: string;
}

export interface CitySummary {
  city: string;
  country_code?: string;
  country?: string;
  places: number;
  visits: number;
  seconds: number;
  last_visit: string;
}

export interface CountrySummary {
  country_code: string;
  country?: string;
  cities: number;
  places: number;
  visits: number;
  seconds: number;
  last_visit: string;
}

export interface PlaceChoice {
  id?: string;
  osm_ref?: string;
  name?: string;
  category?: PlaceCategory;
  lat?: number;
  lon?: number;
  address?: string;
  city?: string;
  country_code?: string;
  country?: string;
}

export interface NearbyPlace extends PlaceChoice {
  source: 'yours' | 'group' | 'map';
  distance_m: number;
}

export interface CityRef {
  city: string;
  country_code?: string;
  country?: string;
}

export interface TripAway {
  start: string;
  end: string;
  ongoing?: boolean;
  home: CityRef;
  nights: number;
  days: string[];
  cities: CityRef[];
  stays: { name?: string; address?: string; category: PlaceCategory; city?: string; start: string; end: string }[];
}

export interface TimelineImport {
  id: string;
  format: string;
  file_name: string;
  status: 'processing' | 'done' | 'failed';
  error?: string;
  first_day?: string;
  last_day?: string;
  stays_added: number;
  trips_added: number;
  stays_skipped: number;
  trips_skipped: number;
  days_too_old: number;
  /** What a Google Takeout archive added to activities and health history, and left out (spec 006 FR-120). */
  activities_added?: number;
  activities_skipped?: number;
  activities_too_old?: number;
  step_days_added?: number;
  hr_hours_added?: number;
  resting_days_added?: number;
  sleep_added?: number;
  readings_added?: number;
  health_skipped?: number;
  health_too_old?: number;
  /** timeline_off says the file's location history was not kept, since the long-term timeline is off. */
  timeline_off?: boolean;
  /** height_cm is the height the import set on the member, who had none. */
  height_cm?: number;
  /** location and fit say what the file held: location history, and Google Fit workouts or health data (spec 005 FR-076). */
  location?: boolean;
  fit?: boolean;
  created_at: string;
  finished_at?: string;
}

// The timeline explorer (spec 005): views of the member's own timeline.

export interface Explorer {
  routes: { mode: TravelMode; line: string }[];
  flights: string[];
  bounds?: [number, number, number, number];
  tiles: {
    zoom: number;
    count: number;
    max_square: number;
    max_square_at?: [number, number];
    max_cluster: number;
    new_this_month: number;
    cells: [number, number, number][];
  };
}

export interface CountNew {
  count: number;
  new: number;
}

export interface RecapTotals {
  period: string;
  from: string;
  to: string;
  empty: boolean;
  distance_m: number;
  travel_s: number;
  modes: ModeTotal[];
  places: CountNew;
  cities: CountNew;
  regions: CountNew;
  countries: CountNew;
  nights_away: number;
}

export interface Farthest {
  distance_m: number;
  name?: string;
  place_id?: string;
  lat: number;
  lon: number;
  at: string;
  date: string;
}

export interface TopPlace {
  place_id?: string;
  name?: string;
  address?: string;
  category: PlaceCategory;
  seconds: number;
}

export interface Recap extends RecapTotals {
  farthest?: Farthest;
  busiest_day?: { date: string; places: number };
  longest_travel_day?: { date: string; distance_m: number };
  top_places: TopPlace[];
  previous: RecapTotals;
}

export interface TimelineRecords {
  farthest?: Farthest;
  longest_trip_away?: { start: string; end: string; nights: number; date: string; cities: number };
  longest_travel_day?: { date: string; distance_m: number };
  most_places_day?: { date: string; places: number };
  most_countries_year?: { year: number; countries: number };
  fastest_trip?: { trip_id?: string; mode: TravelMode; start: string; date: string; top_speed_mps: number };
  days_since_trip_away?: number;
}

export interface PlacePatterns {
  place_id: string;
  // hours are seconds there by weekday and hour, Monday first.
  hours: number[][];
  visits: number;
  total_s: number;
  average_s: number;
  longest_s: number;
  months: { month: string; visits: number }[];
  days: string[];
  first_visit?: string;
  last_visit?: string;
  longest_week_streak: number;
}

export interface RegionSummary {
  region: string;
  country_code?: string;
  country?: string;
  places: number;
  visits: number;
  seconds: number;
  first_visit: string;
  last_visit: string;
}

export interface DaysIn {
  year: number;
  countries: { country_code: string; country?: string; days: number }[];
  regions: { region: string; country_code?: string; country?: string; days: number }[];
}

export interface SearchResult {
  kind: 'stay' | 'trip' | 'note';
  date: string;
  start: string;
  end?: string;
  tz?: string;
  entry_id?: string;
  place?: { id: string; name?: string; category: PlaceCategory; address?: string; city?: string; region?: string; country_code?: string; country?: string };
  mode?: TravelMode;
  distance_m?: number;
  note_id?: string;
  note?: string;
}

export interface SearchResults {
  results: SearchResult[];
  more: boolean;
}

export type TimelineMeasure = 'distance' | 'away' | 'places';

export interface TimelineCalendar {
  year: number;
  measure: TimelineMeasure;
  days: { date: string; value: number }[];
  max: number;
  streaks: { away: { longest: number; current: number }; new_place: { longest: number; current: number } };
}

export interface PastDays {
  days: { date: string; places: number; distance_m: number }[];
}
